import { VStack, InputGroup, Input, Box, Text } from '@chakra-ui/react';
import { SearchIcon, VisibilityIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { DefaultCell } from 'components/common/Table/Cells';
import { Table } from 'components/common/Table/Table';
import { ClassWorksheetsData } from 'components/common/Table/types/Column';
import {
  useSearchClassWorksheets,
  WorksheetByClassDTO,
} from 'hooks/api/backend/managament/getClasses';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { createColumns } from 'utils/tableHelper';
import { ProtectedRoutes } from 'types/Routes';
import { Pagination } from 'components/common/Table/Pagination';
import { WorksheetMenu } from '../WorksheetMenu';

type Props = {
  worksheets: WorksheetByClassDTO[] | undefined;
  classId: string;
  className: string;
};

export const ClassWorksheetsContent = ({ worksheets, classId, className }: Props): JSX.Element => {
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const inputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  const { data: searchData, isLoading: isLoadingSearch } = useSearchClassWorksheets({
    classId: classId || '',
    searchQuery: debouncedSearchQuery,
    page: currentPage,
    enabled: !!classId,
  });

  const handlePageChange = (newPage: number): void => {
    setCurrentPage(newPage);
  };

  const classWorksheetsColumns = createColumns<ClassWorksheetsData>()({
    title: {
      header: 'Titel',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.primary',
        labelWeight: '500',
      }),
    },
    sharedBy: {
      header: 'Geteilt von',
      sortDisabled: true,
      element: DefaultCell,
      getCellProps: () => ({
        textColor: 'text.secondary',
      }),
    },
    results: {
      header: 'Ergebnisse',
      sortDisabled: true,
    },
    actionItem: {
      header: 'Aktion',
      sortDisabled: true,
    },
  });
  const handleWsClick = useCallback(
    (id: string): void => {
      // TODO: preselect school of class in worksheet review page
      const nextParams = new URLSearchParams({ mode: 'review', t: '1' });
      if (classId) {
        nextParams.set('classId', classId);
      }
      if (className) {
        nextParams.set('className', className);
      }
      navigate(`${ProtectedRoutes.WORKSHEET_DETAIL}/${id}?${nextParams.toString()}`);
    },
    [navigate, classId, className]
  );

  const mappedClassWorksheets = useMemo(() => {
    const classWorksheets =
      debouncedSearchQuery && searchData ? searchData.worksheets : worksheets || [];

    return classWorksheets.map((classWorksheet) => {
      const sharedByName = classWorksheet.createdBy
        ? `${classWorksheet.createdBy.firstName} ${classWorksheet.createdBy.lastName}`.trim() || '–'
        : '–';
      return {
        id: classWorksheet.id,
        title: classWorksheet.title,
        sharedBy: sharedByName,
        results: `${classWorksheet.answerCount} / ${classWorksheet.assignedCount}`,
        actionItem: (
          <WorksheetMenu
            items={[
              {
                icon: <NovaIcon icon={VisibilityIcon} size="s" aria-hidden />,
                label: 'Ansehen',
                onClick: (e) => {
                  e.stopPropagation();
                  handleWsClick(classWorksheet.id);
                },
              },
            ]}
          />
        ),
      };
    });
  }, [worksheets, handleWsClick, debouncedSearchQuery, searchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.length >= 1 ? searchQuery : '');
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  return (
    <VStack align="flex-start" gap="30px">
      {/* WS Header */}
      <VStack align="flex-start" gap="l" width="100%">
        <Text textStyle="body.m.medium" color="text.secondary">
          Arbeitsblätter ({mappedClassWorksheets?.length || 0})
        </Text>
        {/* Search Input */}
        <InputGroup
          startElement={<NovaIcon icon={SearchIcon} size="m" color="icon.accent" aria-hidden />}
        >
          <Input
            ref={inputRef}
            placeholder="Arbeitsblatt suchen"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="background.canvas"
            border="default"
            borderColor="border.accent"
            borderRadius="10px"
            h="44px"
            _placeholder={{ color: 'text.quaternary' }}
            _focus={{
              borderColor: 'border.accent.active',
              boxShadow: 'none',
            }}
          />
        </InputGroup>
      </VStack>

      {/* Worksheets Table */}
      <Box width="100%">
        <Table<ClassWorksheetsData>
          columns={classWorksheetsColumns}
          data={mappedClassWorksheets}
          isDataLoading={isLoadingSearch}
          tableRowProps={{
            cursor: 'pointer',
            _hover: {
              bg: 'background.canvas',
            },
          }}
          onRowClick={handleWsClick}
        />
        {searchData && searchData.totalPages > 1 && (
          <Pagination
            totalPages={searchData.totalPages}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </Box>
    </VStack>
  );
};
